import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import ModuleData, { isPlacable, SingularModuleData } from '../../../types/ModuleData'
import Droppable from '../dnd/Droppable'
import getDragType from '../dnd/getDragType'
import Tooltip from '../Tooltip'
import PreviewModule, { getActualModuleOffset } from './PreviewModule'

export type PreviewSingularModuleProps = {
  module: SingularModuleData
  systemWidth: number
  scale: number
  selected: boolean
  setSelected: (id: string, nextSelected: boolean) => unknown
  disabled?: boolean
  delay: number
  onTooltipClick: (module: ModuleData) => unknown
  ignoreClose?: string
}

export const SingularModuleWrap = styled.div<{ module: SingularModuleData; scale: number }>`
  position: absolute;
  bottom: ${({ module: { bottom }, scale }) => (bottom ?? 0) * scale}px;
  height: ${({ module: { height }, scale }) => height * scale}px;
  left: 0;
  width: 100%;
  display: flex;
  flexdirection: column;
`

const DroppableSingular = styled(Droppable)`
  flex: 1;
`

const getTooltipLeft = (systemWidth: number, module: ModuleData | undefined, scale: number): number => {
  if (!isPlacable(module)) {
    return 0
  }
  const { left } = getActualModuleOffset(systemWidth, module, scale)
  return Math.round(left + module.width * scale)
}

const PreviewSingularModule = React.memo<PreviewSingularModuleProps>(function PreviewSingularModule({
  module,
  systemWidth,
  scale,
  disabled,
  selected,
  setSelected,
  delay,
  onTooltipClick,
  ignoreClose
}) {
  const type = getDragType(module)
  const { left, right } = getActualModuleOffset(systemWidth, module, scale)
  const items = useMemo(() => (selected ? [module.id] : []), [module.id, selected])
  const updateVariableModules = useCallback(
    (item: string, from: number | undefined, to: number | undefined) => {
      setSelected(module.id, typeof to !== 'undefined')
    },
    [module.id, setSelected]
  )
  const acceptsItem = useCallback(
    (args: { draggableId: string; item: string; droppableSourceId: string; droppableTargetId: string }) => {
      if (args.droppableSourceId === args.droppableTargetId) {
        return true
      }
      return !selected
    },
    [selected]
  )

  return (
    <SingularModuleWrap module={module} scale={scale}>
      <DroppableSingular
        type={type}
        highlightInsetLeft={left}
        highlightInsetRight={right}
        disabled={disabled}
        items={items}
        updateItems={updateVariableModules}
        acceptsItem={acceptsItem}
      >
        {(item, i, items, provided) => {
          return (
            <Tooltip
              top="50%"
              left={getTooltipLeft(systemWidth, module, scale) + 'px'}
              title={module?.title}
              delay={delay}
              onClick={() => onTooltipClick(module)}
              data-cs-ignore-close={ignoreClose}
            >
              <PreviewModule module={module} scale={scale} {...provided.dragHandleProps} />
            </Tooltip>
          )
        }}
      </DroppableSingular>
    </SingularModuleWrap>
  )
})

export default PreviewSingularModule
